import { z } from 'zod';
import { phoneSchema } from '@shared/utils/phone';
import { addressLine1Schema, addressLine2Schema, addressLine3Schema, postalCodeSchema, requiredAddressLine1Schema, requiredCitySchema, requiredPostalCodeSchema } from '@shared/components/AddressFields/addressSchema';

/**
 * Validation schema for the profile form.
 *
 * Kept out of ProfileInfo so it can be tested on its own — importing the component drags in the
 * whole profile barrel, and through it httpClient and config.js, which reads import.meta.env and
 * cannot be parsed under Jest.
 *
 * @param {boolean} canViewBillingAndContact Whether the role sees the billing and contact fields.
 * @param {boolean} hasContactOptions Whether any main contact is available to choose.
 * @param {boolean} [canEditCompany] Whether the role can edit the company name (account owner).
 */
export const createRoleBasedSchema = (canViewBillingAndContact, hasContactOptions, canEditCompany = false) => {
  const baseSchema = {
    // Same rule as the signup form's company name: trimmed, so a spaces-only name reads as empty
    // and surrounding spaces never reach the database or HaloPSA. Enforced only for the role that
    // can edit the field — for everyone else it is read-only, and a stored name they cannot change
    // must not block them from saving their own details.
    firmName: canEditCompany
      ? z.string()
          .trim()
          .min(1, 'Company name is required.')
          .min(2, 'Company name must be at least 2 characters.')
          .max(200, 'Company name must be 200 characters or less.')
      : z.string().max(200, 'Company name must be 200 characters or less.'),
    firstName: z.string()
      .min(1, 'First name is required.')
      .regex(/^[A-Za-z]+$/, 'First name must contain only letters.')
      .max(64, 'First name must be 64 characters or less.'),
    lastName: z.string()
      .min(1, 'Last name is required.')
      .regex(/^[A-Za-z]+$/, 'Last name must contain only letters.')
      .max(64, 'Last name must be 64 characters or less.'),
    username: z.string()
      .min(1, 'Username is required.')
      .max(100, 'Username must be 100 characters or less.'),
    email: z.email('Invalid email address.'),
    // The signed-in user's OWN phone number — distinct from contactPhone below, which belongs to
    // the firm's main contact (a different person). Shared with the Team page's Add/Edit User
    // phone field and the signup form (identical HaloPSA contact field under the hood) via
    // @shared/utils/phone.
    phone: phoneSchema,
    address1: z.string().optional(),
    // Same 255 cap as shippingAddress2 (and as checkout / Edit Billing Details). Without it, "Same
    // as billing" copied an over-long Line 2 into the hidden shipping field, which then failed with
    // no field on screen to fix.
    address2: addressLine2Schema,
    address3: addressLine3Schema,
    region: z.string().optional(), // Optional since some countries have no regions
    postalCode: z.string().optional(),
    // Billing-only, so it is required below rather than here — see the note in that branch.
    country: z.string().optional(),
    // Shipping ("Site Address") — mounts only in ShippingAddressSection (same gating as Billing
    // above), and country is shared with billing rather than repeated here. Declared regardless of
    // role like the billing address lines above, so zodResolver doesn't strip them from `data`
    // for a role that can't see the section — the values are simply always empty for those roles.
    shippingAddress1: addressLine1Schema,
    shippingAddress2: addressLine2Schema,
    shippingAddress3: addressLine3Schema,
    shippingRegion: z.string().optional(),
    shippingPostalCode: postalCodeSchema,
    shippingSameAsBilling: z.boolean().optional(),
  };

  // Only add contact validation if user can view billing and contact sections
  if (canViewBillingAndContact) {
    // Required only for the roles that can actually see the country dropdown. It lives in
    // BillingAddressSection, which only mounts for those roles — and the value itself is only ever
    // set from inside that section (useBillingAddress), so requiring it of every role left a
    // License Admin or Registered Contact permanently invalid on a field they could neither see nor
    // fill, with no error text on screen and a Save button that silently did nothing. This mirrors
    // UpdateMyProfileRequestValidator, which likewise validates the billing address only for these
    // roles, and UpdateMyProfileService, which discards it for the others.
    baseSchema.country = z.string().min(1, 'Country is required.');
    // Same reasoning as country above — required only where the section (and thus the field)
    // is actually visible. Mirrors UpdateMyProfileRequestValidator's Address1/Postal Code rules.
    // Address Line 2 stays optional (base schema above already leaves it so).
    baseSchema.address1 = requiredAddressLine1Schema;
    baseSchema.address3 = requiredCitySchema;
    baseSchema.postalCode = requiredPostalCodeSchema;
    baseSchema.shippingAddress1 = requiredAddressLine1Schema;
    baseSchema.shippingAddress3 = requiredCitySchema;
    baseSchema.shippingPostalCode = requiredPostalCodeSchema;
    baseSchema.billingEmail = z.string()
      .min(1, 'Billing email is required.')
      .pipe(z.email('Please enter a valid email address.').max(255, 'Billing email must be 255 characters or less'));
    // Required: a company always has a main contact, and the Halo billing-account name is kept in
    // step with whoever holds it. Enforced only for roles that can see the field, and only when there
    // are contacts to choose from — otherwise a firm whose contact list fails to load would lock the
    // owner out of saving anything at all, including their own name.
    baseSchema.mainContactId = hasContactOptions
      ? z
          .string()
          .min(1, 'Main contact is required.')
          .refine((v) => v !== '0', 'Main contact is required.')
      : z.string().optional();
    baseSchema.mainContactUsername = z.string().optional();
    // Keep old fields for backward compatibility when not editing
    baseSchema.contactName = z.string().optional();
    baseSchema.contactEmail = z.string().optional();
    baseSchema.contactPhone = z.string().optional();
  } else {
    baseSchema.contactName = z.string().optional();
    baseSchema.contactEmail = z.string().optional();
    baseSchema.contactPhone = z.string().optional();
  }

  return z.object(baseSchema);
};
